import {
  ref, onMounted, watch,
} from 'vue';
import { fireTag } from '@vue-util/tagManager';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import { brand } from 'config';
import useFacetOptions from './useFacetOptions';
import useSortFilter from './useSortFilter';
import useFacets from './useFacets';
import useConvoAllFiltersButton from './useConvoAllFiltersButton';
import useOneClickSaleFilter from './useOneClickSaleFilter';
import { setRestorePosition } from './useRestoreScroll';

export default function useFacetsList() {
  const {
    triggerSelectedFacetsUpdate,
    isSlideoutMode,
    facets,
    toggleDrawer,
    sortBySelectedValue,
    globalOptions,
    setOpenedDropdown,
    openedDropdown,
  } = useFacets();

  const { setActiveDropdown, facetsOptions } = useFacetOptions();
  const { buildSections, setAllFiltersButtonClicked, allFiltersButtonClicked } = useSortFilter();
  const isConvoAllFiltersButton = useConvoAllFiltersButton();
  const { selectOneClickSaleFilter, deselectOneClickSaleFilter } = useOneClickSaleFilter();
  const enableAsyncAnalyticsTags = globalOptions?.consumerStore?.state?.envProps?.enableAsyncAnalyticsTags;

  const onFacetClick = (facet) => {
    const isAllFiltersButtonClicked = facet.selected.id === 'filter';
    setAllFiltersButtonClicked(isAllFiltersButtonClicked);
    /* istanbul ignore else */
    if (isAllFiltersButtonClicked) {
      setOpenedDropdown(null); // explicitely close all the  dropdowns when right slideout is opened
      /* istanbul ignore else */
      if (brand === 'bcom' && !isConvoAllFiltersButton.value) {
        if (enableAsyncAnalyticsTags) {
          useFireTagAsync(() => fireTag('link', {
            event_name: 'horizontal facet all filters',
            link_name: 'horizontal facet all filters',
          }));
        } else {
          fireTag('link', {
            event_name: 'horizontal facet all filters',
            link_name: 'horizontal facet all filters',
          });
        }
      }
    }
    if (isSlideoutMode.value || facet.selected.alwaysDropdown || isAllFiltersButtonClicked) {
      buildSections({
        facetId: facet.selected.id,
        sortBySelectedValue: sortBySelectedValue.value,
        facets: facets.value,
      });
      if (facet.selected.alwaysDropdown) {
        setActiveDropdown(facet.dropdownRef);
        setOpenedDropdown(facet.selected.dropdown?.isOpened ? facet.selected : null);
        facet.dropdownRef.scrollTop = 0;
      } else if (facet.selected.id === 'one-click-sale') {
        if (facet.selected.isSelected) {
          deselectOneClickSaleFilter();
        } else {
          selectOneClickSaleFilter();
        }
      } else {
        /* istanbul ignore else */
        if (facet.selected.id !== 'filter') {
          if (enableAsyncAnalyticsTags) {
            useFireTagAsync(() => fireTag('link', {
              event_name: 'horizontal facets',
              link_name: `${facet.selected.tagVal}`,
            }));
          } else {
            fireTag('link', {
              event_name: 'horizontal facets',
              link_name: `${facet.selected.tagVal}`,
            });
          }
          /* istanbul ignore else */
          if (brand === 'bcom') {
            if (enableAsyncAnalyticsTags) {
              useFireTagAsync(() => fireTag('link', {
                event_name: 'facet expand',
                facet_names: [`${facet.selected.tagVal}`],
                facet_values: ['no facet'],
              }));
            } else {
              fireTag('link', {
                event_name: 'facet expand',
                facet_names: [`${facet.selected.tagVal}`],
                facet_values: ['no facet'],
              });
            }
          }
        }
        toggleDrawer();
        /* istanbul ignore else */
        if (brand === 'mcom') {
          if (enableAsyncAnalyticsTags) {
            useFireTagAsync(() => fireTag('link', { link_name: 'search_browse_filter_expand' }));
          } else {
            fireTag('link', { link_name: 'search_browse_filter_expand' });
          }
        }
      }
      /* istanbul ignore else */
      if (globalOptions.onFacetClickedEvent) {
        globalOptions.onFacetClickedEvent();
      }
    } else {
      setActiveDropdown(facet.dropdownRef);
    }
  };

  const onCloseFacetDropdown = () => {
    setActiveDropdown(null);
    setRestorePosition();
    /* istanbul ignore else */
    if (globalOptions.pageType === 'registry') {
      triggerSelectedFacetsUpdate();
    }
  };

  const positionDropdown = (facet) => {
    facet.dropdownRef.style.width = '400px';
    const {
      x, width,
    } = facet.dropdownRef.getBoundingClientRect();
    const outsideScreenByWidth = x + width > document?.body.clientWidth;
    /* istanbul ignore else */
    if (outsideScreenByWidth && facet.selected.id !== 'facet_DELIVERY_AND_PICKUP') {
      facet.dropdownRef.style.transform = `translateX(${document?.body.clientWidth - (x + width)}px)`;
    }
  };

  const onFacetClickLocal = (facet) => {
    onFacetClick(facet);
    /* istanbul ignore else */
    if (facet.selected.dropdown?.isOpened) {
      positionDropdown(facet);
    }
  };

  const isReady = ref(false);

  onMounted(() => {
    isReady.value = true;
  });

  watch(() => [openedDropdown.value, allFiltersButtonClicked.value], ([newDrop, newSlide], [oldDrop, oldSlide]) => {
    /* istanbul ignore else */
    if (newDrop !== oldDrop || newSlide !== oldSlide) {
      /* istanbul ignore else */
      if (globalOptions.slideoutFacets.value) {
        globalOptions.slideoutFacets.value.totalSelectedFacets = globalOptions.totalFacetSelectedCount.value;
        globalOptions.slideoutFacets.value.productCount = globalOptions.productCount.value;
      }
    }
  });

  return {
    facetsOptions,
    onFacetClick,
    onCloseFacetDropdown,
    onFacetClickLocal,
    isProductsLoading: globalOptions.isProductsLoading,
  };
}
